import OverlayTrigger from '@rio-cloud/rio-uikit/OverlayTrigger';
import Tooltip from '@rio-cloud/rio-uikit/Tooltip';
import { useEffect, useRef,useState } from 'react';
import { useIntl } from 'react-intl';
import { useMediaQuery } from 'react-responsive';
import {useNavigate} from "react-router-dom";

import AnimatedRobot from '@/components/atom/AnimatedRobot/AnimatedRobot';
import AnimatedRobotSpeechBubble, {
  RobotMessage,
} from '@/components/molecule/AnimatedRobotSpeechBubble/AnimatedRobotSpeechBubble';
import {
  Container,
  StyledButton
} from "@/components/organism/OnboardingIntroductionContent/OnboardingIntroductionContent.styles";
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { setCurrentPage } from '@/store/about/aboutSlice';
import { setCurrentLayout } from '@/store/layout/layoutSlice';
import {
  activateOnboardingMode,
  disableDemoFlow,
  disableOnboardingMode,
  enableDemoFlow,
  resetStep,
  selectIsDemoFlowFinish,
  selectIsOnboardingMode,
} from '@/store/onboarding/onboardingSlice';
import { closeSidebar, selectIsSidebarOpen } from '@/store/sidebar/sidebarSlice';
import { addNewThread, removeOnboardingThread, resetActiveThread } from '@/store/thread/threadSlice';
import { setShowTips } from '@/store/tips/tipsSlice';

type Steps = {
  [key in number]: RobotMessage;
};

const texts = {
  stepOneTitle: 'chatPage.sidebar.onboardingMode.stepOne.title',
  stepOneText: 'chatPage.sidebar.onboardingMode.stepOne.text',
  stepOneButtonText: 'chatPage.sidebar.onboardingMode.stepOne.buttonText',
  stepOneAltButtonText: 'chatPage.sidebar.onboardingMode.stepOne.showTips.button',
  stepTwoTitle: 'chatPage.sidebar.onboardingMode.stepTwo.title',
  stepTwoText: 'chatPage.sidebar.onboardingMode.stepTwo.text',
  stepThreeTitle: 'chatPage.sidebar.onboardingMode.stepThree.title',
  stepThreeText: 'chatPage.sidebar.onboardingMode.stepThree.text',
  stepFourTitle: 'chatPage.sidebar.onboardingMode.stepFour.congrats.title',
  stepFourText: 'chatPage.sidebar.onboardingMode.stepFour.congrats.text',
  stepFourChatButton: 'chatPage.sidebar.onboardingMode.stepFour.chat.button',
  stepFourPromptButton: 'chatPage.sidebar.onboardingMode.stepFour.prompt.button',
  disableOnboarding: 'chatPage.sidebar.onboardingMode.disable',
  activateOnboarding: 'chatPage.sidebar.onboardingMode.activate',
  onboardingActiveLabel: 'chatPage.sidebar.onboardingMode.active.label',
  onboardingInactiveLabel: 'chatPage.sidebar.onboardingMode.inactive.label',
};

const OnboardingIntroductionContent = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isOnboardingMode = useAppSelector(selectIsOnboardingMode);
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  const isDemoFlowFinish = useAppSelector(selectIsDemoFlowFinish);
  const isSmallScreen = useMediaQuery({ query: '(max-width: 47.5rem)' });

  const navigate = useNavigate();

  const [messages, setMessages] = useState<RobotMessage[]>([]);
  const [isRobotVisible, setIsRobotVisible] = useState(false);
  const chatContainerRef = useRef(null);

  const scrollToBottom = () => {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: 'smooth',
    });
  };

  const addMessage = (newMessage: RobotMessage) => {
    setMessages((prev) => {
      const alreadyExists = prev.some((msg) => msg.title === newMessage.title);
      if (!alreadyExists) {
        return [...prev, newMessage];
      }
      return prev;
    });
  };

  const clearMessages = () => {
    setMessages([]);
  };

  const steps: Steps = {
    0: {
      title: texts.stepOneTitle,
      text: texts.stepOneText,
      buttons: [
        {
          text: texts.stepOneButtonText,
          onClick: () => {
            dispatch(activateOnboardingMode());
            dispatch(enableDemoFlow());
            dispatch(resetStep());
            addMessage(steps[1]);
            if (isSmallScreen) {
              dispatch(closeSidebar());
            }
          },
        },
        {
          text: texts.stepOneAltButtonText,
          onClick: () => {
            dispatch(setShowTips(true));
          },
        },
      ],
    },
    1: {
      title: texts.stepTwoTitle,
      text: texts.stepTwoText,
      buttons: [],
    },
    2: {
      title: texts.stepThreeTitle,
      text: texts.stepThreeText,
      buttons: [],
    },
    3: {
      title: texts.stepFourTitle,
      text: texts.stepFourText,
      buttons: [
        {
          text: texts.stepFourChatButton,
          onClick: () => {
            dispatch(disableDemoFlow());
            dispatch(removeOnboardingThread());
            dispatch(disableOnboardingMode());
            dispatch(addNewThread());
            dispatch(resetActiveThread());
            dispatch(setCurrentLayout('chat'));
            if (isSmallScreen) {
              dispatch(closeSidebar());
            }
            navigate('/chat', { replace: true });
          },
        },
        {
          text: texts.stepFourPromptButton,
          onClick: () => {
            dispatch(setCurrentLayout('about'));
            dispatch(setCurrentPage('page4'));
            navigate('/about', { replace: true });
          },
        },
      ],
    },
  };

  useEffect(() => {
    if (isSidebarOpen) {
      setTimeout(() => setIsRobotVisible(true), 700);

      setTimeout(() => setMessages([steps[0]]), 1500);
    } else {
      setIsRobotVisible(false);
      setMessages([]);
    }
  }, [isSidebarOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isDemoFlowFinish) {
      clearMessages();
      addMessage(steps[3]);
    }
  }, [isDemoFlowFinish]);

  const handleToggleOnboarding = () => {
    clearMessages();

    if (isOnboardingMode) {
      dispatch(disableOnboardingMode());
      addMessage(steps[0]);
    } else {
      dispatch(activateOnboardingMode());
      addMessage(steps[0]);
      addMessage(steps[1]);
    }
  };

  return (
    <Container>
      <OverlayTrigger
        placement="left"
        overlay={
          <Tooltip width="auto">
            {intl.formatMessage({ id: isOnboardingMode ? texts.disableOnboarding : texts.activateOnboarding })}
          </Tooltip>
        }
      >
        <StyledButton
          className={`btn btn-outline ${isOnboardingMode ? 'btn-primary' : 'btn-muted'}`}
          onClick={handleToggleOnboarding}
        />
      </OverlayTrigger>
      <div>
        <AnimatedRobotSpeechBubble messages={messages} />
        <AnimatedRobot isVisible={isRobotVisible} />
        <div ref={chatContainerRef} style={{ position: 'absolute', bottom: '0' }} />
      </div>
    </Container>
  );
};

export default OnboardingIntroductionContent;

